import React, { useState, useMemo, useRef } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { CustomIcon } from "../shared/CustomIcon";
import { apiCreateGiving, apiCreateExpense } from "./api";

// ============================================================
// FinanceImportModal — CSV / XLSX import for Giving or Expenses.
//
// Same phased UX as the members BulkImportPage:
//   1. Upload file → auto-map columns
//   2. Editable column-mapping grid + 10-row preview
//   3. Confirm → per-row create → success/failure summary
//
// The two modes differ only in target-field vocab + per-row
// validation:
//   • Giving:  member name resolved case-insensitively against
//              `members`. Unmatched rows offer Skip / Anonymous /
//              Manual pick. Amount → cents. PM defaults E-Transfer.
//   • Expense: payee is free text. Category matched case-insensitive
//              to the allowed vocab; unknown → "Other". All Pending.
// ============================================================

const GIVING_CATEGORIES  = ["Regular Offering", "Tithe", "Seed", "Alms", "Other"];
const EXPENSE_CATEGORIES = [
  "Mortgage/Rent", "Staff Wages", "Guest Speakers", "Equipment/Materials",
  "Pastor Stipend", "Utilities", "Legal Fees", "Bank Fees", "Benevolence",
  "Missionary Support", "Staff Travel", "Ministry Education", "Licenses",
  "Insurances", "Payroll Taxes", "Other",
];
const PAYMENT_METHODS    = ["Cash", "Cheque", "E-Transfer", "Card"];

const GIVING_FIELDS = [
  { key: "date",           label: "Date *",           required: true,  aliases: ["date", "given on", "giving date", "transaction date", "when"] },
  { key: "memberName",     label: "Member Name",      aliases: ["name", "member", "member name", "giver", "donor", "full name"] },
  { key: "amount",         label: "Amount *",         required: true,  aliases: ["amount", "amt", "value", "dollars", "sum", "total"] },
  { key: "category",       label: "Category",         aliases: ["category", "type", "purpose"] },
  { key: "paymentMethod",  label: "Payment Method",   aliases: ["method", "payment", "payment method", "pay method"] },
  { key: "notes",          label: "Notes",            aliases: ["notes", "note", "memo", "comment"] },
];

const EXPENSE_FIELDS = [
  { key: "date",     label: "Date *",     required: true,  aliases: ["date", "expense date", "when"] },
  { key: "payee",    label: "Payee *",    required: true,  aliases: ["payee", "vendor", "paid to", "recipient", "name"] },
  { key: "amount",   label: "Amount *",   required: true,  aliases: ["amount", "amt", "value", "dollars", "sum"] },
  { key: "category", label: "Category",   aliases: ["category", "type"] },
  { key: "notes",    label: "Notes",      aliases: ["notes", "note", "memo", "description", "comment"] },
];

function dollarsToCents(v) {
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

// Best-effort parse of a spreadsheet date cell into ISO YYYY-MM-DD.
// Uses LOCAL date parts (never `toISOString`) so a user's July 13 stays
// July 13 regardless of their timezone offset.
function normalizeDate(raw) {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  // ISO already
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const toLocalISO = (d) => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  // Excel serial number (days since 1899-12-30) — convert without UTC drift.
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (n > 25000 && n < 60000) {
      // Excel epoch is 1899-12-30 local. Add whole days as local calendar days.
      const d = new Date(1899, 11, 30);
      d.setDate(d.getDate() + Math.floor(n));
      return toLocalISO(d);
    }
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) return toLocalISO(d);
  return s;
}

function autoMap(headers, targetFields) {
  const map = {};
  targetFields.forEach(f => {
    const h = headers.find(h =>
      f.aliases.some(a => a.toLowerCase() === String(h || "").trim().toLowerCase())
    );
    if (h) map[f.key] = h;
  });
  return map;
}

function matchCategoryCI(raw, allowed) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return null;
  return allowed.find(c => c.toLowerCase() === s) || null;
}

function matchMemberCI(raw, members) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return null;
  return members.find(m => {
    const full = `${m.firstName || ""} ${m.lastName || ""}`.trim().toLowerCase();
    return full === s;
  }) || null;
}

// -------------------------------------------------------------
export default function FinanceImportModal({ open, kind, members, currencySymbol = "$", onClose, onSaved }) {
  const isGiving = kind === "giving";
  const targetFields = isGiving ? GIVING_FIELDS : EXPENSE_FIELDS;

  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  // Per-row resolutions for the giving flow: `{ [rowIdx]: { action: "skip"|"anonymous"|"member", memberId?: string } }`
  const [resolutions, setResolutions] = useState({});
  const inputRef = useRef(null);

  const activeMembers = useMemo(
    () => (members || []).filter(m => m.status !== "inactive" && !m.archived),
    [members]
  );

  // Preview rows (up to 10) with normalized values.
  const previewRows = useMemo(() => {
    return rawRows.slice(0, 10).map(r => {
      const obj = {};
      targetFields.forEach(f => {
        obj[f.key] = mapping[f.key] ? String(r[mapping[f.key]] ?? "").trim() : "";
      });
      return obj;
    });
  }, [rawRows, mapping, targetFields]);

  // Per-row validation (used for row count summary + resolutions UI).
  const rowStatus = useMemo(() => {
    if (rawRows.length === 0) return [];
    return rawRows.map((r, idx) => {
      const get = (k) => mapping[k] ? String(r[mapping[k]] ?? "").trim() : "";
      const date = normalizeDate(get("date"));
      const amountCents = dollarsToCents(get("amount"));
      const issues = [];
      if (!date) issues.push("Missing date");
      if (amountCents <= 0) issues.push("Missing or invalid amount");
      if (isGiving) {
        const nm = get("memberName");
        const matched = matchMemberCI(nm, activeMembers);
        return {
          idx,
          date, amountCents,
          rawMember: nm,
          matchedMember: matched,
          rawCategory:  get("category"),
          rawPayment:   get("paymentMethod"),
          notes:        get("notes"),
          issues,
        };
      } else {
        const rawCat = get("category");
        const cat = matchCategoryCI(rawCat, EXPENSE_CATEGORIES);
        if (!get("payee")) issues.push("Missing payee");
        return {
          idx,
          date, amountCents,
          payee: get("payee"),
          matchedCategory: cat,
          rawCategory: rawCat,
          notes: get("notes"),
          issues,
        };
      }
    });
  }, [rawRows, mapping, activeMembers, isGiving]);

  // Count rows that need attention: unmatched name (giving) or unrecognized category (expense).
  const rowsNeedingAttention = useMemo(() => {
    if (rawRows.length === 0) return 0;
    if (isGiving) {
      return rowStatus.filter(r => r.issues.length === 0 && r.rawMember && !r.matchedMember).length;
    }
    return rowStatus.filter(r => r.issues.length === 0 && r.rawCategory && !r.matchedCategory).length;
  }, [rowStatus, rawRows, isGiving]);

  if (!open) return null;

  const closeAndReset = () => {
    setRawHeaders([]); setRawRows([]); setMapping({}); setError("");
    setSummary(null); setResolutions({});
    if (inputRef.current) inputRef.current.value = "";
    onClose && onClose();
  };

  const applyParsed = (heads, rows) => {
    if (!rows || rows.length === 0) { setError("File contains no data rows."); return; }
    setRawHeaders(heads);
    setRawRows(rows);
    setMapping(autoMap(heads, targetFields));
    setResolutions({});
  };

  const onPick = async (e) => {
    setError(""); setSummary(null);
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const ext = f.name.split(".").pop().toLowerCase();
      if (ext === "csv") {
        Papa.parse(f, {
          header: true, skipEmptyLines: true,
          complete: (res) => {
            applyParsed(res.meta?.fields || [], res.data || []);
            setBusy(false);
          },
          error: (err) => { setError(err.message || "CSV parse failed."); setBusy(false); },
        });
      } else if (ext === "xlsx" || ext === "xls") {
        const buf = await f.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const heads = json.length ? Object.keys(json[0]) : [];
        applyParsed(heads, json);
        setBusy(false);
      } else {
        setError("Unsupported file type. Use .csv, .xlsx, or .xls.");
        setBusy(false);
      }
    } catch (ex) {
      setError(ex.message || "Could not read the file.");
      setBusy(false);
    }
  };

  const importNow = async () => {
    setError("");
    if (!mapping.date || !mapping.amount) {
      setError("Map both Date and Amount before importing.");
      return;
    }
    if (!isGiving && !mapping.payee) {
      setError("Map the Payee column before importing.");
      return;
    }
    setImporting(true);
    let created = 0, skipped = 0;
    const failed = [];

    for (const row of rowStatus) {
      const rowNum = row.idx + 2; // header is row 1
      if (row.issues.length > 0) {
        failed.push({ row: rowNum, reason: row.issues.join("; ") });
        continue;
      }
      try {
        if (isGiving) {
          const res = resolutions[row.idx];
          let body;
          if (row.matchedMember) {
            body = {
              isAnonymous: false,
              memberId: row.matchedMember.id,
              date: row.date, amountCents: row.amountCents,
              category: matchCategoryCI(row.rawCategory, GIVING_CATEGORIES) || "Regular Offering",
              paymentMethod: matchCategoryCI(row.rawPayment, PAYMENT_METHODS) || "E-Transfer",
              notes: row.notes || undefined,
            };
          } else if (res?.action === "skip" || (!res && row.rawMember)) {
            // Unresolved unmatched row → skip by default.
            skipped++; continue;
          } else if (res?.action === "anonymous" || (!res && !row.rawMember)) {
            body = {
              isAnonymous: true,
              memberId: null,
              date: row.date, amountCents: row.amountCents,
              category: matchCategoryCI(row.rawCategory, GIVING_CATEGORIES) || "Regular Offering",
              paymentMethod: matchCategoryCI(row.rawPayment, PAYMENT_METHODS) || "E-Transfer",
              notes: row.notes || undefined,
            };
          } else if (res?.action === "member" && res.memberId) {
            body = {
              isAnonymous: false,
              memberId: res.memberId,
              date: row.date, amountCents: row.amountCents,
              category: matchCategoryCI(row.rawCategory, GIVING_CATEGORIES) || "Regular Offering",
              paymentMethod: matchCategoryCI(row.rawPayment, PAYMENT_METHODS) || "E-Transfer",
              notes: row.notes || undefined,
            };
          } else {
            skipped++; continue;
          }
          await apiCreateGiving(body);
        } else {
          const finalCat = row.matchedCategory || "Other";
          const needsCategoryOther = finalCat === "Other";
          const body = {
            payeeName:   row.payee,
            payeeType:   "Vendor",   // Imports default to Vendor; users can edit after import.
            date:        row.date,
            amountCents: row.amountCents,
            category:    finalCat,
            // When matching failed but raw text existed, preserve it. When category is
            // "Other" but no source text was provided, use a placeholder so the
            // backend's "categoryOther required when category=Other" check passes.
            categoryOther: needsCategoryOther
              ? (row.rawCategory && matchCategoryCI(row.rawCategory, EXPENSE_CATEGORIES) == null
                    ? row.rawCategory
                    : "Imported (uncategorized)")
              : undefined,
            notes:       row.notes || undefined,
            // Backend sets approvalStatus to Pending by default.
          };
          await apiCreateExpense(body);
        }
        created++;
      } catch (e) {
        failed.push({ row: rowNum, reason: e?.message || "Save failed" });
      }
    }
    setImporting(false);
    setSummary({
      total: rowStatus.length,
      created, skipped, failed,
    });
    onSaved && onSaved();
  };

  const setRowResolution = (idx, patch) => {
    setResolutions(prev => ({ ...prev, [idx]: { ...(prev[idx] || {}), ...patch } }));
  };

  const readyRowCount = rowStatus.filter(r => r.issues.length === 0).length;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && closeAndReset()}
      data-testid={`finance-import-modal-${kind}`}
      style={{ alignItems: "flex-start", paddingTop: 12, paddingBottom: 84 }}
    >
      <div
        className="modal"
        style={{
          maxWidth: 640,
          width: "min(96vw, 640px)",
          maxHeight: "calc(100vh - 96px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER (sticky) */}
        <div className="modal-header" style={{ position: "sticky", top: 0, background: "inherit", zIndex: 2 }}>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <CustomIcon name="download-inbox" size={18} />
            Import {isGiving ? "Giving" : "Expenses"} — CSV or XLSX
          </h3>
          <button className="modal-close" onClick={closeAndReset} data-testid={`finance-import-${kind}-close`}>×</button>
        </div>

        {/* BODY (scrolls) */}
        <div style={{ overflowY: "auto", flex: 1, padding: 16 }} data-testid={`finance-import-${kind}-body`}>
          {/* STEP 1 — file picker */}
          {rawRows.length === 0 && !summary && (
            <>
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 12 }}>
                Upload a <b>.csv</b>, <b>.xlsx</b> or <b>.xls</b> file.
                We&apos;ll auto-map common column names — you can adjust any mapping before confirming.
              </div>
              <input
                ref={inputRef}
                type="file"
                data-testid={`finance-import-${kind}-file`}
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={onPick}
                disabled={busy}
                style={{ fontFamily: "Montserrat,sans-serif", fontSize: 12, color: "var(--kc-text)" }}
              />
              <div style={{ marginTop: 14, padding: 12, borderRadius: 8, background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", fontSize: 12, lineHeight: 1.5 }}>
                <b>Expected columns</b> (case-insensitive):{" "}
                {isGiving
                  ? <span>Date, Name (or Member/Donor), Amount, Category (optional), Payment Method (optional), Notes (optional)</span>
                  : <span>Date, Payee (or Vendor), Amount, Category (optional), Notes (optional)</span>}
              </div>
              <div style={{ marginTop: 10, fontSize: 11, opacity: 0.7 }}>
                Amounts are treated as dollar values and stored in cents.
                {isGiving
                  ? " Unmatched names can be skipped, imported as Anonymous, or manually linked to a member."
                  : " All imported expenses start as Pending approval — they must be approved before appearing in totals."}
              </div>
              {busy && <div style={{ marginTop: 10, fontSize: 13, opacity: 0.7 }}>Reading file…</div>}
              {error && (
                <div data-testid={`finance-import-${kind}-error`} style={{ marginTop: 10, background: "rgba(248,113,113,0.15)", color: "#f87171", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>{error}</div>
              )}
            </>
          )}

          {/* STEP 2 — mapping + preview */}
          {rawRows.length > 0 && !summary && (
            <>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
                Column mapping ({rawRows.length} data rows)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10 }}>
                {targetFields.map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 10, color: f.required ? "var(--kc-accent)" : "var(--kc-sub)", letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>{f.label}</label>
                    <select
                      className="form-input"
                      data-testid={`finance-import-${kind}-map-${f.key}`}
                      value={mapping[f.key] || ""}
                      onChange={e => setMapping(m => ({ ...m, [f.key]: e.target.value }))}
                    >
                      <option value="">— Not mapped —</option>
                      {rawHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              {/* Status ribbon */}
              <div style={{ marginTop: 14, padding: 10, background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 8, fontSize: 12 }}>
                <b>{readyRowCount}</b> of {rawRows.length} rows ready to import.
                {rowsNeedingAttention > 0 && (
                  <> · <span style={{ color: "#f59e0b", fontWeight: 700 }}>{rowsNeedingAttention} need attention</span></>
                )}
              </div>

              {/* Preview */}
              <div style={{ marginTop: 12, fontSize: 12, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
                Preview — first {previewRows.length} of {rawRows.length}
              </div>
              <div style={{ marginTop: 6, overflowX: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", borderRadius: 8 }}>
                <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }} data-testid={`finance-import-${kind}-preview`}>
                  <thead>
                    <tr style={{ background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)", color: "var(--kc-accent)" }}>
                      {targetFields.map(f => (
                        <th key={f.key} style={{ padding: "6px 8px", textAlign: "left", whiteSpace: "nowrap" }}>{f.label.replace(" *", "")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                        {targetFields.map(f => (
                          <td key={f.key} style={{ padding: "5px 8px", whiteSpace: "nowrap" }}>
                            {r[f.key] || <span style={{ color: f.required ? "#f87171" : "rgba(255,255,255,0.4)" }}>{f.required ? "missing" : "—"}</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Per-row resolutions for Giving (unmatched names) */}
              {isGiving && rowsNeedingAttention > 0 && (
                <div style={{ marginTop: 16 }} data-testid="finance-import-giving-resolutions">
                  <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, color: "#f59e0b" }}>
                    Unmatched names — pick an action
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {rowStatus
                      .filter(r => r.issues.length === 0 && r.rawMember && !r.matchedMember)
                      .map(r => {
                        const res = resolutions[r.idx] || { action: "skip" };
                        return (
                          <div key={r.idx} style={{ padding: 10, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 8 }}>
                            <div style={{ fontSize: 12, marginBottom: 6 }}>
                              Row {r.idx + 2}: <b>{r.rawMember}</b> — {currencySymbol}{(r.amountCents / 100).toFixed(2)} on {r.date}
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              {["skip", "anonymous", "member"].map(action => (
                                <label key={action} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                                  <input
                                    type="radio"
                                    name={`res-${r.idx}`}
                                    checked={res.action === action}
                                    onChange={() => setRowResolution(r.idx, { action })}
                                    data-testid={`finance-import-giving-res-${r.idx}-${action}`}
                                  />
                                  {action === "skip" ? "Skip" : action === "anonymous" ? "Import as Anonymous" : "Pick Member"}
                                </label>
                              ))}
                              {res.action === "member" && (
                                <select
                                  className="form-input"
                                  value={res.memberId || ""}
                                  onChange={e => setRowResolution(r.idx, { memberId: e.target.value })}
                                  data-testid={`finance-import-giving-res-${r.idx}-pick`}
                                  style={{ fontSize: 12, flex: "1 1 200px", minWidth: 160 }}
                                >
                                  <option value="">— Select member —</option>
                                  {activeMembers.map(m => <option key={m.id} value={m.id}>{m.lastName}, {m.firstName}</option>)}
                                </select>
                              )}
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
              )}

              {/* Note for Expense unknown categories */}
              {!isGiving && rowsNeedingAttention > 0 && (
                <div style={{ marginTop: 14, padding: 10, borderRadius: 8, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.35)", fontSize: 12 }} data-testid="finance-import-expense-cat-note">
                  <b>{rowsNeedingAttention}</b> row{rowsNeedingAttention === 1 ? "" : "s"} have category values that don&apos;t match the allowed vocab
                  {" "}(<i>{EXPENSE_CATEGORIES.join(", ")}</i>). These will be imported under <b>Other</b> with the original text preserved.
                </div>
              )}

              {error && (
                <div data-testid={`finance-import-${kind}-error`} style={{ marginTop: 10, background: "rgba(248,113,113,0.15)", color: "#f87171", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>{error}</div>
              )}
            </>
          )}

          {/* STEP 3 — success + failure summary */}
          {summary && (
            <div data-testid={`finance-import-${kind}-summary`}>
              <div style={{ textAlign: "center", padding: "16px 8px" }}>
                <div style={{ fontSize: 48, color: "#4ade80" }}>✓</div>
                <div style={{ fontFamily: "'Cinzel',serif", fontSize: 20, fontWeight: 700, marginTop: 6 }}>
                  Import Complete
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8, marginTop: 8 }}>
                <SumStat label="Total rows" value={summary.total} testid={`finance-import-${kind}-stat-total`} />
                <SumStat label="Created"    value={summary.created} color="#4ade80" testid={`finance-import-${kind}-stat-created`} />
                <SumStat label="Skipped"    value={summary.skipped} color="#f59e0b" testid={`finance-import-${kind}-stat-skipped`} />
                <SumStat label="Failed"     value={summary.failed.length} color="#f87171" testid={`finance-import-${kind}-stat-failed`} />
              </div>
              {summary.failed.length > 0 && (
                <div style={{ marginTop: 14, padding: 12, background: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 8 }} data-testid={`finance-import-${kind}-failed-list`}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#f87171", marginBottom: 6 }}>
                    Failed rows:
                  </div>
                  <ul style={{ margin: "4px 0 0 18px", fontSize: 12, color: "#f2a2a2" }}>
                    {summary.failed.slice(0, 12).map((f, i) => (
                      <li key={i}>Row {f.row} — {f.reason}</li>
                    ))}
                    {summary.failed.length > 12 && <li>… and {summary.failed.length - 12} more</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER (sticky) */}
        <div style={{
          position: "sticky", bottom: 0, background: "var(--kc-surface)",
          borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)",
          padding: "12px 16px",
          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        }}>
          {!summary && rawRows.length > 0 && (
            <>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => { setRawHeaders([]); setRawRows([]); setMapping({}); setResolutions({}); }}
                data-testid={`finance-import-${kind}-cancel`}
              >
                Cancel
              </button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                className="btn"
                onClick={importNow}
                disabled={importing || readyRowCount === 0}
                data-testid={`finance-import-${kind}-confirm`}
                style={{
                  background: readyRowCount > 0 ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 35%, transparent)",
                  color: "#111", fontWeight: 800,
                  minHeight: 44, padding: "0 22px", borderRadius: 10,
                  cursor: importing || readyRowCount === 0 ? "not-allowed" : "pointer",
                }}
              >
                {importing ? "Importing…" : `Import ${readyRowCount} row${readyRowCount === 1 ? "" : "s"}`}
              </button>
            </>
          )}
          {(summary || rawRows.length === 0) && (
            <button className="btn btn-primary" onClick={closeAndReset} style={{ marginLeft: "auto" }} data-testid={`finance-import-${kind}-done`}>
              {summary ? "Done" : "Close"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SumStat({ label, value, color = "var(--kc-accent)", testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        padding: 10,
        borderRadius: 8,
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "'Cinzel',serif" }}>{value}</div>
      <div style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginTop: 2 }}>{label}</div>
    </div>
  );
}
